/**
 * Pure matching core (no I/O).
 *
 * Rules:
 * - male / female only (opposite-sex pairing)
 * - pool must be verified + approved when built from UserApplication
 * - values score weights: C 30% · H 25% · L 25% · P 20%
 * - weekly suggestions capped at 4 (never pad)
 * - Match only on mutual interest (both liked) — see interestEngine
 */

import type { MatchProfile } from "./types";
import { oppositeGender } from "./types";
import { valuesScore } from "./valuesScore";
import type { GateCode } from "./gates";
import { evaluateCountryGate, evaluateGates } from "./gates";
import type { AffinityAxis, AffinityBreakdown } from "./affinity";
import { AFFINITY_AXES, affinityScore } from "./affinity";
import {
  clampWeeklyLimit,
  MIN_SHOWN_SCORE,
  WEEKLY_SUGGESTION_DEFAULT,
  WEEKLY_SUGGESTION_MAX,
  WEEKLY_SUGGESTION_MIN,
} from "./weights";

/**
 * Soft boost for people who already liked you. Near-ties go to mutual
 * interest first — speed-to-partner — without jumping a weak fit over a
 * strong one (boost is well under a full affinity band).
 */
export const RECIPROCITY_BOOST = 7;

/**
 * Soft boost when the viewer's accepted backgrounds (nationality / ancestry
 * preference) include the candidate's declared own origins. Preference only —
 * never a hard gate while ANCESTRY_MODE === "preference".
 */
export const NATIONALITY_PREF_BOOST = 4;

/** Within this many points of the best remaining score, prefer a different dominant axis. */
export const DIVERSITY_SCORE_MARGIN = 8;

export {
  valuesScore,
  individualValuesScore,
  scoreLabel,
  scaleDiffs,
} from "./valuesScore";
export {
  VALUE_WEIGHTS,
  WEEKLY_SUGGESTION_MIN,
  WEEKLY_SUGGESTION_MAX,
  WEEKLY_SUGGESTION_DEFAULT,
  clampWeeklyLimit,
  DAILY_SUGGESTION_MIN,
  DAILY_SUGGESTION_MAX,
  DAILY_SUGGESTION_DEFAULT,
  clampDailyLimit,
} from "./weights";
export {
  isEligibleForMatching,
  getIneligibilityReasons,
  matchProfileFromApplication,
  gendersCompatible,
} from "./eligibility";

export {
  expressInterest,
  hasLiked,
  hasPassed,
  hasReverseLike,
  hasActed,
  findActiveMatch,
  actedTargetIds,
  matchedPartnerIds,
  otherUserId,
  upsertInterestInList,
} from "./interestEngine";

export type {
  Interest,
  InterestStatus,
  MutualMatch,
  ExpressInterestResult,
  ExpressInterestInput,
} from "./interestTypes";
export { pairKey, sortedUserIds } from "./interestTypes";

export interface RankedCandidate {
  profile: MatchProfile;
  /** Existing four-axis score. Always present — no screen loses its number. */
  valuesScore: number;
  rank: number;
  /**
   * UN8 affinity, present only when BOTH sides answered the
   * questionnaire. Undefined means this pair was ranked on the old model.
   */
  affinity?: AffinityBreakdown;
}

/** A candidate the gates removed. Not shown to members — review/debug only. */
export interface GatedOutCandidate {
  profileId: string;
  failed: GateCode[];
}

export interface DailyDeckResult {
  dateKey: string;
  limit: number;
  candidates: RankedCandidate[];
}

export function utcDateKey(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Ranking key. Affinity when the pair is fully answered, the existing
 * four-axis score otherwise. Both are 0–100, so a mixed population — the
 * normal state during rollout — sorts on one comparable scale.
 */
export function rankingScore(row: {
  valuesScore: number;
  affinity?: AffinityBreakdown;
}): number {
  return row.affinity ? row.affinity.total : row.valuesScore;
}

/** Pair score stored on mutual matches — affinity when both answered. */
export function pairAlignmentScore(self: MatchProfile, other: MatchProfile): number {
  if (self.answers && other.answers) {
    return affinityScore(
      self.answers,
      other.answers,
      self.scales,
      other.scales
    ).total;
  }
  return valuesScore(self.scales, other.scales);
}

function effectiveScore(
  row: { valuesScore: number; affinity?: AffinityBreakdown; profile: MatchProfile },
  preferIds?: ReadonlySet<string>,
  self?: MatchProfile
): number {
  let base = rankingScore(row);
  if (preferIds?.has(row.profile.id)) {
    base = Math.min(100, base + RECIPROCITY_BOOST);
  }
  // Nationality / ancestry preference (soft) — accepted list ∩ candidate own.
  const accepted = self?.answers?.ancestry?.accepted ?? [];
  const own = row.profile.answers?.ancestry?.own ?? [];
  if (
    accepted.length > 0 &&
    !accepted.includes("any") &&
    own.length > 0 &&
    own.some((o) => accepted.includes(o))
  ) {
    base = Math.min(100, base + NATIONALITY_PREF_BOOST);
  }
  return base;
}

/** Strongest scored affinity axis, or null when legacy-only. */
export function dominantAffinityAxis(
  affinity?: AffinityBreakdown
): AffinityAxis | null {
  if (!affinity) return null;
  let best: AffinityAxis | null = null;
  let bestScore = -1;
  for (const axis of AFFINITY_AXES) {
    const s = affinity.axes[axis];
    if (s > bestScore) {
      bestScore = s;
      best = axis;
    }
  }
  return bestScore > 0 ? best : null;
}

/**
 * From a score-sorted list, pick up to `limit` so the weekly four are not
 * four near-clones of the same affinity shape. When totals are within
 * DIVERSITY_SCORE_MARGIN, prefer a candidate whose dominant axis is not
 * already represented.
 */
export function selectDiverseCandidates(
  ranked: RankedCandidate[],
  limit: number
): RankedCandidate[] {
  const cap = clampWeeklyLimit(limit);
  if (ranked.length <= cap) {
    return ranked.map((row, i) => ({ ...row, rank: i + 1 }));
  }

  const remaining = ranked.map((row) => ({ ...row }));
  const selected: RankedCandidate[] = [];
  const usedAxes = new Set<AffinityAxis>();

  while (selected.length < cap && remaining.length > 0) {
    let maxScore = -1;
    for (const row of remaining) {
      maxScore = Math.max(maxScore, rankingScore(row));
    }

    let bestIdx = 0;
    let bestKey = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const row = remaining[i];
      const score = rankingScore(row);
      if (score < maxScore - DIVERSITY_SCORE_MARGIN) continue;
      const dom = dominantAffinityAxis(row.affinity);
      const freshAxis = dom && !usedAxes.has(dom) ? 1 : 0;
      // Diversity first among near-ties, then raw score, then stable id.
      const key = freshAxis * 10_000 + score * 10 + (1 - i / remaining.length);
      if (key > bestKey) {
        bestKey = key;
        bestIdx = i;
      }
    }

    const [pick] = remaining.splice(bestIdx, 1);
    selected.push(pick);
    const dom = dominantAffinityAxis(pick.affinity);
    if (dom) usedAxes.add(dom);
  }

  return selected.map((row, i) => ({ ...row, rank: i + 1 }));
}

/**
 * Rank opposite-sex candidates.
 *
 * Two stages, in this order:
 *   1. hard gates — a failure removes the candidate entirely, it is never
 *      ranked low and shown anyway. Skipped when either side has no answers.
 *   2. weighted affinity — used as the ranking key when both sides answered,
 *      otherwise the existing values score stands.
 *
 * Then diversity selection among near-ties, with an optional reciprocity
 * boost for people who already liked you.
 *
 * Excludes already liked/passed/matched IDs. Returns up to `limit` (≤4).
 */
export function rankCandidates(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  excludedIds: ReadonlySet<string>;
  limit?: number;
  /** Closed-test demo fallback — rank on values score when gates zero the deck. */
  skipGates?: boolean;
  /** Inbound likers — soft-boosted into the weekly four when scores are close. */
  preferIds?: ReadonlySet<string>;
}): RankedCandidate[] {
  const limit = clampWeeklyLimit(input.limit ?? WEEKLY_SUGGESTION_DEFAULT);
  const want = oppositeGender(input.self.gender);
  const selfAnswers = input.self.answers;
  const preferIds = input.preferIds;

  const scored = input.pool
    .filter(
      (p) =>
        p.id !== input.self.id &&
        p.gender === want &&
        !input.excludedIds.has(p.id)
    )
    // stage 1 — hard gates, both directions. Unanswered never blocks.
    .filter((p) => {
      if (input.skipGates) return true;
      if (!evaluateCountryGate(input.self, p).passed) return false;
      if (!selfAnswers || !p.answers) return true;
      return evaluateGates(selfAnswers, p.answers).passed;
    })
    // stage 2 — score
    .map((profile) => ({
      profile,
      valuesScore: valuesScore(input.self.scales, profile.scales),
      rank: 0,
      affinity:
        selfAnswers && profile.answers
          ? affinityScore(
              selfAnswers,
              profile.answers,
              input.self.scales,
              profile.scales
            )
          : undefined,
    }))
    .filter((row) => rankingScore(row) >= MIN_SHOWN_SCORE)
    .sort((a, b) => {
      const sa = effectiveScore(a, preferIds, input.self);
      const sb = effectiveScore(b, preferIds, input.self);
      if (sb !== sa) return sb - sa;
      // Stable secondary: inbound likers before non-likers at equal score
      const pa = preferIds?.has(a.profile.id) ? 1 : 0;
      const pb = preferIds?.has(b.profile.id) ? 1 : 0;
      if (pb !== pa) return pb - pa;
      return a.profile.id.localeCompare(b.profile.id);
    });

  return selectDiverseCandidates(scored, limit);
}

/**
 * Why each candidate was removed. Not part of the member-facing flow —
 * this exists so the review console can answer "why is my pool empty?".
 */
export function gatedOutCandidates(input: {
  self: MatchProfile;
  pool: MatchProfile[];
}): GatedOutCandidate[] {
  const selfAnswers = input.self.answers;
  const want = oppositeGender(input.self.gender);
  const out: GatedOutCandidate[] = [];
  for (const p of input.pool) {
    if (p.id === input.self.id || p.gender !== want) continue;
    const countryResult = evaluateCountryGate(input.self, p);
    if (!countryResult.passed) {
      out.push({ profileId: p.id, failed: countryResult.failed });
      continue;
    }
    if (!p.answers || !selfAnswers) continue;
    const result = evaluateGates(selfAnswers, p.answers);
    if (!result.passed) out.push({ profileId: p.id, failed: result.failed });
  }
  return out;
}

export function buildDailyDeck(input: {
  self: MatchProfile;
  pool: MatchProfile[];
  excludedIds: ReadonlySet<string>;
  limit?: number;
  now?: Date;
  skipGates?: boolean;
  preferIds?: ReadonlySet<string>;
}): DailyDeckResult {
  const now = input.now ?? new Date();
  const candidates = rankCandidates(input);
  return {
    dateKey: utcDateKey(now),
    limit: clampWeeklyLimit(input.limit ?? WEEKLY_SUGGESTION_DEFAULT),
    candidates,
  };
}

/** Documented constants for consumers */
export const MATCHING_RULES = {
  genders: ["male", "female"] as const,
  pairing: "opposite_sex" as const,
  requiresVerified: true,
  requiresApprovedVerification: true,
  weeklySuggestionsCap: WEEKLY_SUGGESTION_MAX,
  weeklySuggestionsMin: WEEKLY_SUGGESTION_MIN,
  /** @deprecated aliases — weekly is the product rule */
  dailySuggestionsMin: WEEKLY_SUGGESTION_MIN,
  dailySuggestionsMax: WEEKLY_SUGGESTION_MAX,
  weights: {
    commitment: 0.3,
    honesty: 0.25,
    loyalty: 0.25,
    passion: 0.2,
  },
  matchRequiresMutualInterest: true,
} as const;
